import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend, ComposedChart, ReferenceLine } from "recharts";
import { refShort, brl, num } from "../lib/format";

export const COLORS = ["#0B74C0", "#38BDF8", "#059669", "#D97706", "#E11D48", "#0B1F3A", "#64748B", "#14B8A6"];
const grid = <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />;
const tip = (fmt) => <Tooltip formatter={(v, n) => [fmt ? fmt(v) : num(v, 1), n]} labelFormatter={(l) => (/^\d{4}-\d{2}$/.test(l) ? refShort(l) : l)} contentStyle={{ borderRadius: 10, border: "1px solid #E2E8F0", fontSize: 12 }} />;
const xRef = <XAxis dataKey="period" tickFormatter={refShort} tickLine={false} axisLine={false} />;
// React 19 treats a data field named `ref` as a React ref when recharts spreads entries, so rename it
const norm = (data) => (data || []).map(({ ref, ...x }) => ({ ...x, period: ref }));
const money = (v) => brl(v);
const kfmt = (v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : v);

export const AreaSeries = ({ data, dataKey, name, money: m, h = 240, color = COLORS[0] }) => (
  <ResponsiveContainer width="100%" height={h}>
    <AreaChart data={norm(data)} margin={{ left: -10, right: 8, top: 8 }}>
      <defs><linearGradient id={`g-${dataKey}`} x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.25} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient></defs>
      {grid}{xRef}<YAxis tickLine={false} axisLine={false} tickFormatter={m ? kfmt : undefined} />{tip(m ? money : null)}
      <Area type="monotone" dataKey={dataKey} name={name} stroke={color} strokeWidth={2.2} fill={`url(#g-${dataKey})`} />
    </AreaChart>
  </ResponsiveContainer>
);

export const CostLimitChart = ({ data, limit, h = 260, color = COLORS[2] }) => {
  const max = Math.max(limit || 0, ...(data || []).map((d) => Math.max(d.cost || 0, d.forecast || 0)));
  return (
    <ResponsiveContainer width="100%" height={h}>
      <AreaChart data={norm(data)} margin={{ left: -10, right: 12, top: 16 }}>
        <defs><linearGradient id="g-ben-cost" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={color} stopOpacity={0.25} /><stop offset="100%" stopColor={color} stopOpacity={0} /></linearGradient>
          <linearGradient id="g-ben-fc" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D97706" stopOpacity={0.12} /><stop offset="100%" stopColor="#D97706" stopOpacity={0} /></linearGradient></defs>
        {grid}{xRef}<YAxis tickLine={false} axisLine={false} tickFormatter={kfmt} domain={[0, Math.ceil(max * 1.1)]} />{tip(money)}<Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
        <Area type="monotone" dataKey="cost" name="Custo com benefícios" stroke={color} strokeWidth={2.2} fill="url(#g-ben-cost)" />
        <Area type="linear" dataKey="forecast" name="Estimativa" stroke="#D97706" strokeWidth={2} strokeDasharray="5 4" fill="url(#g-ben-fc)" dot={{ r: 2.5 }} />
        {limit > 0 && <ReferenceLine y={limit} stroke="#E11D48" strokeDasharray="6 4" strokeWidth={1.5} label={{ value: `Limite ${brl(limit)}`, position: "insideTopRight", fill: "#E11D48", fontSize: 11 }} />}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export const MultiLine = ({ data, lines, h = 240, money: m }) => (
  <ResponsiveContainer width="100%" height={h}>
    <LineChart data={norm(data)} margin={{ left: -10, right: 8, top: 8 }}>
      {grid}{xRef}<YAxis tickLine={false} axisLine={false} tickFormatter={m ? kfmt : undefined} />{tip(m ? money : null)}<Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      {lines.map((l, i) => <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={COLORS[i]} strokeWidth={2} dot={false} />)}
    </LineChart>
  </ResponsiveContainer>
);

export const GroupBars = ({ data, bars, h = 240, money: m, line }) => (
  <ResponsiveContainer width="100%" height={h}>
    <ComposedChart data={norm(data)} margin={{ left: -10, right: 8, top: 8 }}>
      {grid}{xRef}<YAxis tickLine={false} axisLine={false} tickFormatter={m ? kfmt : undefined} />{tip(m ? money : null)}<Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
      {bars.map((b, i) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color || COLORS[i]} radius={[4, 4, 0, 0]} maxBarSize={22} />)}
      {line && <Line type="monotone" dataKey={line.key} name={line.name} stroke={COLORS[3]} strokeWidth={2} dot={false} />}
    </ComposedChart>
  </ResponsiveContainer>
);

export const HBars = ({ data, h = 240, color = COLORS[0] }) => (
  <ResponsiveContainer width="100%" height={h}>
    <BarChart data={data} layout="vertical" margin={{ left: 10, right: 16 }}>
      <XAxis type="number" hide /><YAxis type="category" dataKey="name" width={120} tickLine={false} axisLine={false} />{tip()}
      <Bar dataKey="value" name="Funcionários" fill={color} radius={[0, 4, 4, 0]} maxBarSize={16} />
    </BarChart>
  </ResponsiveContainer>
);

export const Donut = ({ data, h = 220 }) => (
  <ResponsiveContainer width="100%" height={h}>
    <PieChart>
      <Pie data={data} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="85%" paddingAngle={2}>
        {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
      </Pie>
      {tip()}<Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
    </PieChart>
  </ResponsiveContainer>
);
